import React, { useCallback, useEffect, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";
import { Group } from "@scrile/api-provider/dist/api/GroupsProvider";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { Topic } from "@scrile/api-provider/dist/api/TopicsProvider";
import { debounce } from "lodash-es";
import { Routes } from "router/config";
import { useTranslation } from "locales";
import useAuthUser from "hooks/useAuthUser";
import emitter, { EVENTS } from "lib/emitter";
import { MediaFilesController } from "lib/MediaFileController";
import { cleanMessagePreview } from "lib/messageMetadata";
import { cacheTopics, getCachedTopics } from "lib/offlineCache";
import providers from "lib/providers";
import "./style.scss";

interface Props {
  group: Group;
  onBack?: () => void;
  onOpenSettings?: () => void;
}

interface TopicRow {
  // null for the implicit, non-deletable "General" topic.
  topicId: string | null;
  name: string;
  thread: MessageThread | null;
}

function ArrowLeft() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="22" height="22">
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
      <path
        d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"
        fill="currentColor"
      />
    </svg>
  );
}

// Group Topics (Темы) overview: shown when a topics-enabled group is opened
// without a specific topic selected. Lists the General topic + every custom
// topic as a navigable row (last message preview + unread badge), Telegram-style.
function TopicsList({ group, onBack, onOpenSettings }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const { user: authUser } = useAuthUser();
  const [rows, setRows] = useState<TopicRow[]>([]);
  const [loading, setLoading] = useState(true);
  const isOwner = !!authUser && String(group.ownerId) === String(authUser.id);

  const load = useCallback(async () => {
    if (!authUser) return;
    try {
      // Two parallel fetches:
      //   1. Topics list.
      //   2. ALL backing threads of the group in ONE query (no topicId
      //      filter), then bucket by `thread.topicId` on the client. This
      //      replaces the previous 1+N pattern (one query per topic) with
      //      1+1 — a meaningful speedup for groups with many topics, and
      //      avoids the browser's parallel-request queueing limit.
      //
      // `size` is over-estimated to cover general + custom topics. We don't
      // know the topic count yet, but groups realistically have <30 topics,
      // and the API caps it anyway. We pass 50 as a safe upper bound.
      const [topics, allThreadsRes] = await Promise.all([
        providers.TopicsProvider.search(group.id),
        providers.MessageThreadsProvider.find({
          data: {
            userId: String(authUser.id),
            subjectType: "group",
            subjectId: group.id,
            size: 50,
          },
        }).catch(() => ({ result: [] } as any)),
      ]);

      const threadsByTopicId: Record<string, MessageThread> = {};
      for (const thread of allThreadsRes.result || []) {
        const key = thread.topicId == null ? "general" : String(thread.topicId);
        // First-write-wins — provider already sorts by activity, so the
        // first thread seen for a topic is its latest.
        if (!threadsByTopicId[key]) threadsByTopicId[key] = thread;
      }

      const base: Array<{ topicId: string | null; name: string }> = [
        { topicId: null, name: t("General") },
        ...topics.map((topic) => ({ topicId: topic.id, name: topic.name })),
      ];
      const withThreads = base.map((row) => ({
        ...row,
        thread: threadsByTopicId[row.topicId == null ? "general" : String(row.topicId)] || null,
      }));
      setRows(withThreads);

      // Persist for instant paint on next open. Fire-and-forget — cache
      // failure should never block the live UI.
      cacheTopics(String(group.id), topics, threadsByTopicId).catch(() => {});
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to load topics:", e);
      // Fall back to a single General row ONLY if the screen is still empty.
      // If cache hydration already painted real topics, keep them — clobbering
      // them with a one-item fallback on a transient network error would be a
      // visible regression (cached content disappearing on flaky networks).
      setRows((prev) => (prev.length > 0 ? prev : [{ topicId: null, name: t("General"), thread: null }]));
    } finally {
      setLoading(false);
    }
  }, [authUser, group.id, t]);

  // Cache-first paint: hydrate the rows synchronously from IndexedDB before
  // the fresh-fetch path lands. Stable React keys (`row.topicId || "general"`)
  // preserve identity across the swap → no remount / no visible reorder.
  //
  // `freshLoaded` guards the rare race where the fresh fetch wins the network
  // race against an unusually slow IDB read (cold-start lock contention). If
  // fresh data has already painted, the late cache hit must NOT overwrite it
  // with stale rows.
  useEffect(() => {
    let cancelled = false;
    let freshLoaded = false;
    setLoading(true);
    (async () => {
      try {
        const cached = await getCachedTopics(String(group.id));
        if (cancelled || freshLoaded || !cached) return;
        const base: Array<{ topicId: string | null; name: string }> = [
          { topicId: null, name: t("General") },
          ...cached.topics.map((topic: Topic) => ({ topicId: topic.id, name: topic.name })),
        ];
        const withThreads = base.map((row) => ({
          ...row,
          thread: cached.threadsByTopicId[row.topicId == null ? "general" : String(row.topicId)] || null,
        }));
        setRows(withThreads);
        setLoading(false);
      } catch {
        /* cache miss — fall through to live load */
      }
    })();
    load().finally(() => {
      freshLoaded = true;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Refresh when topics change (immediate) or a new message lands. The latter
  // fires for every message app-wide and each load() is 1+N queries, so it is
  // debounced to avoid a refetch storm in a busy multi-topic group.
  useEffect(() => {
    const refreshNow = () => load();
    const refreshDebounced = debounce(() => load(), 500);
    emitter.on(EVENTS.TOPICS_CHANGED, refreshNow);
    emitter.on(EVENTS.MESSAGES_NEW_MESSAGE, refreshDebounced);
    return () => {
      emitter.off(EVENTS.TOPICS_CHANGED, refreshNow);
      emitter.off(EVENTS.MESSAGES_NEW_MESSAGE, refreshDebounced);
      refreshDebounced.cancel();
    };
  }, [load]);

  const openTopic = useCallback(
    (topicId: string | null) => {
      history.push(
        generatePath(Routes.groupMessageTopic, { groupId: group.slug || group.id, topicId: topicId || "general" })
      );
    },
    [history, group.slug, group.id]
  );

  const avatarSrc = group.avatarUrl ? MediaFilesController.getImageThumbnail(group.avatarUrl, "240p") : null;
  const initial = group.name ? group.name.charAt(0).toUpperCase() : "G";

  return (
    <div className="topics-list">
      <div className="topics-list__header">
        {onBack && (
          <div className="topics-list__back" onClick={onBack}>
            <ArrowLeft />
          </div>
        )}
        <div className="topics-list__avatar">{avatarSrc ? <img src={avatarSrc} alt={group.name} /> : initial}</div>
        <div className="topics-list__title">
          <div className="topics-list__group-name">{group.name}</div>
          <div className="topics-list__subtitle">{t("Topics")}</div>
        </div>
        {isOwner && onOpenSettings && (
          <button type="button" className="topics-list__settings" title={t("Manage topics")} onClick={onOpenSettings}>
            <GearIcon />
          </button>
        )}
      </div>

      <div className="topics-list__rows">
        {loading && rows.length === 0 && <div className="topics-list__empty">{t("Loading...")}</div>}
        {rows.map((row) => {
          const unread = row.thread?.unreadMessageCount || 0;
          // Same cleaner as the chat-list preview: strips __SYSTEM__/__META__
          // prefixes, [[insights:...]] markers and localises known templates
          // (raw markers used to leak verbatim here).
          const rawPreview = row.thread?.lastMessage?.text || "";
          const preview = rawPreview ? cleanMessagePreview(rawPreview, t) : "";
          return (
            <div key={row.topicId || "general"} className="topics-list__row" onClick={() => openTopic(row.topicId)}>
              <div className="topics-list__row-icon">#</div>
              <div className="topics-list__row-body">
                <div className="topics-list__row-name">{row.name}</div>
                {preview && <div className="topics-list__row-preview">{preview}</div>}
              </div>
              {unread > 0 && <div className="topics-list__badge">{unread > 99 ? "99+" : unread}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TopicsList;
